import { FC, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import { AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { INotifDetailSectionProps } from './types';

// Common text styles to reduce duplication
const commonTextStyles = {
  fontSize: '14px',
  lineHeight: '24px',
  fontWeight: 'bold',
  color: '#606060',
};

// Common reset styles
const commonResetStyles = {
  margin: 0,
  padding: 0,
  border: 'none',
};

const StyledAccordion = styled(MuiAccordion)({
  borderRadius: '12px!important',
  margin: '0!important',
  boxShadow: 'none',
  border: '1px solid #DFDFDF',
  backgroundColor: '#ffffff',
  padding: '0px 24px 0',
  '&:before': {
    display: 'none',
  },
});

const StyledAccordionSummary = styled(AccordionSummary)({
  padding: '12px 0',
  ...commonTextStyles,
  backgroundColor: 'transparent',
  borderRadius: '0!important',
  borderBottom: 'none!important',

  '&.Mui-expanded': {
    ...commonResetStyles,
    minHeight: 0,
    borderRadius: '8px 8px 0 0!important',
  },

  '& .MuiAccordionSummary-content': {
    ...commonResetStyles,
    ...commonTextStyles,

    '&.Mui-expanded': {
      padding: '12px 0',
      margin: 0,
      minHeight: 0,
    },
  },

  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(270deg)',

    '&.Mui-expanded': {
      transform: 'rotate(0deg)',
    },

    '& .MuiSvgIcon-root': {
      color: '#606060',
    },
  },
});

const StyledAccordionDetails = styled(AccordionDetails)({
  padding: '12px 0 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const NotificationDetailSection: FC<INotifDetailSectionProps> = ({ header, children, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  return (
    <StyledAccordion expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
      <StyledAccordionSummary className='border-none border-0' expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.8rem', color: '#ffffff' }} />}>
        {header}
      </StyledAccordionSummary>

      <StyledAccordionDetails>{children}</StyledAccordionDetails>
    </StyledAccordion>
  );
};
